







































































import { computed, defineComponent, PropType, ref } from '@nuxtjs/composition-api';
import useResponsive from '@shared/composables/useResponsive';
import { sanitizeLinkUrl } from '@shared/helpers/sanitizeLinkUrl';
import { BaseButton, UCIcon } from '@shared/ui';

import MegaMenuCard, {
  IMegaMenuCard,
} from '~/components/CMS/NavigationExpandedMenu/MegaMenuCard.vue';

import NavigationMenuItem, {
  INavigationMenuItem,
} from '../NavigationMenuItem/NavigationMenuItem.vue';

export default defineComponent({
  name: 'NavigationExpandedMenu',
  components: {
    MegaMenuCard,
    NavigationMenuItem,
    BaseButton,
    UCIcon,
  },
  props: {
    rootMenuItem: {
      type: Object as PropType<INavigationMenuItem>,
      required: true,
    },
    activeMenuItemsIds: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
  },
  // eslint-disable-next-line max-lines-per-function
  setup(props, { emit }) {
    const { isMobile, isTablet } = useResponsive();

    const scrollableContent = ref<HTMLElement | null>(null);

    const getIsMenuItemOnLevelActive = (menuItem: INavigationMenuItem, level: number) => {
      return menuItem._uid === props.activeMenuItemsIds[level];
    };

    const groupMenuItemsByLevel = (rootMenuItem: INavigationMenuItem): INavigationMenuItem[][] => {
      const result: INavigationMenuItem[][] = [];

      const depthFirstSearch = (node: INavigationMenuItem, depth: number) => {
        if (!result[depth]) result[depth] = [];
        if (node.items?.length) {
          result[depth].push(node);
          node.items.forEach((child) => {
            depthFirstSearch(child, depth + 1);
          });
        }
      };

      depthFirstSearch(rootMenuItem, 0);
      return result;
    };

    const mapArticleToMegaMenuCard = (story: any): IMegaMenuCard => {
      if (story.component !== 'ArticleSelector') {
        return story;
      }
      const {
        article: { content },
      } = story;
      return {
        _uid: story._uid,
        heading: content.title,
        image: content.thumb_image?.filename ? content.thumb_image : content.banner,
        description: content.description,
        button_link: {
          uid: '',
          url: '',
          cached_url: story.article.full_slug,
          linktype: '',
          fieldtype: '',
        },
        button_text:
          content.component === 'ArticlePage'
            ? 'Read more'
            : content.component === 'VideoPage'
            ? 'Watch now'
            : undefined,
      };
    };

    const scrollToRight = () => {
      requestAnimationFrame(() => {
        if (scrollableContent.value) {
          scrollableContent.value.scrollLeft = scrollableContent.value.scrollWidth;
        }
      });
    };

    const isViewAllButtonVisible = (navigationItem: INavigationMenuItem): boolean => {
      if (!navigationItem.link) {
        return false;
      }
      return !!getViewAllLink(navigationItem);
    };

    const getViewAllLink = (navigationItem: INavigationMenuItem): string => {
      return sanitizeLinkUrl(navigationItem?.link?.url || navigationItem?.link?.cached_url);
    };

    const menuBack = () => {
      emit('menu-back');
    };

    const closeNavHeader = () => {
      emit('close-nav-header');
    };

    const getIsLevelVisible = (levelIndex: number) => {
      if (isMobile.value || isTablet.value)
        return levelIndex === props.activeMenuItemsIds.length - 1;
      return levelIndex < props.activeMenuItemsIds.length;
    };

    const getIsMenuItemVisible = (menuItem: INavigationMenuItem, level: number) => {
      return menuItem._uid === props.activeMenuItemsIds[level];
    };

    const getColumnClasses = (level: number) => {
      return {
        'navigation-expanded-menu__column': true,
        'last-visible-column': level === props.activeMenuItemsIds.length - 1,
      };
    };

    const getActiveMenuItemAtLevel = (level: number) => {
      const activeMenuItemId = props.activeMenuItemsIds[level];
      const itemsAtLevel = menuItemsByLevel.value?.[level] ?? [];
      return itemsAtLevel.find((m) => m._uid === activeMenuItemId);
    };

    const menuItemsByLevel = computed(() => groupMenuItemsByLevel(props.rootMenuItem));

    const parentCategoryLabel = computed(
      () => getActiveMenuItemAtLevel(props.activeMenuItemsIds.length - 2)?.label ?? 'Main menu',
    );

    const megaMenuCardsData = computed(() => {
      const currentMenuItem = getActiveMenuItemAtLevel(props.activeMenuItemsIds.length - 1);
      if (!currentMenuItem?.cards?.length) return null;
      const cards = currentMenuItem.cards.map(mapArticleToMegaMenuCard);
      return {
        title: currentMenuItem.cards_title,
        cards: cards,
      };
    });

    return {
      isMobile,
      isTablet,
      scrollableContent,
      menuItemsByLevel,
      megaMenuCardsData,
      parentCategoryLabel,
      scrollToRight,
      isViewAllButtonVisible,
      getViewAllLink,
      getIsMenuItemOnLevelActive,
      menuBack,
      closeNavHeader,
      getIsLevelVisible,
      getIsMenuItemVisible,
      getColumnClasses,
      sanitizeLinkUrl,
    };
  },
});
